import Widget from "./base.js";
import { NewsletterSignupBlock } from "./newsletter-signup-block.js";
import { setCookie, getCookie } from "tpm-utils";
import { Icon } from '../lib/fa-icons.js';

// EmailNag — scroll-triggered overlay nag on the-brief articles (plan:
// tmp/email-nag-plan.md).
//
// PHP gates emission (themes/tpm25/template-parts/email-nag.php, included
// from single-default.php inside .Article__Content): the placeholder only
// exists on singular posts whose primary category is the-brief, for anon
// (membership `none`) readers — and regardless of the EmailWall: both can
// render on the same page, this component paints OVER the wall (z-index)
// until dismissed. This component owns presentation:
//
// - re-checks the category allowlist (defense in depth; mirrors the PHP gate)
// - anchors to the bottom of the 2nd content paragraph, shading everything
//   below it with the overlay (fill to the bottom of .Article__Content)
// - stays hidden until the first downward scroll; repositions on reflow
//   (ResizeObserver on the content + window resize) so it stays glued to p2
// - dismissible sitewide for 1 day via cookie (`tpm_dismiss_emailnag`);
//   the cookie is deliberately client-only: VIP cache variants do not key
//   on it, so the server must never vary output on it
//
// The signup UI is the existing NewsletterSignupBlock (emailwall precedent);
// markup/style are intentionally bare bones.

const EMAILNAG_CATEGORIES = ["the-brief", "cafe", "news"];
const DISMISS_COOKIE = "tpm_dismiss_emailnag";
const DISMISS_DAYS = 1;
const GAP = 8; // breathing room between p2's bottom edge and the overlay top

export class EmailNag extends Widget {
	componentWillMount() {
		this.dismiss = this.dismiss.bind(this);
		this.onResize = this.onResize.bind(this);
		this.position = this.position.bind(this);
		this.lastY = window.scrollY;
		this.raf = null;
	}

	componentDidMount() {
		this.base = this.refs.base;
		if (this.state.dismissed || this.dismissed() || !this.eligible()) {
			this.hide();
			return;
		}

		this.contentEl = this.base.closest(".Article__Content");
		this.anchor = document.querySelector(
			".js_article-content > p:nth-of-type(2)"
		);
		// Fewer than 2 paragraphs (or an unexpected DOM): never show, burn no cookie.
		if (!this.contentEl || !this.anchor) {
			this.hide();
			return;
		}

		// Hidden until the first downward scroll (decision 2). Absolute
		// positioning means display:none has no layout side effects.
		this.position();

		// Stay glued to p2 through reflow (lazy images, ads settling, resize).
		if (typeof ResizeObserver !== "undefined") {
			this.resizeObserver = new ResizeObserver(this.position);
			this.resizeObserver.observe(
				this.contentEl.querySelector(".js_article-content") || this.contentEl
			);
		}
		window.addEventListener("resize", this.onResize);
	}

	componentWillUnmount() {
		this.cleanup();
	}

	dismissed() {
		return getCookie(DISMISS_COOKIE) !== null;
	}

	eligible() {
		return EMAILNAG_CATEGORIES.includes(this.props.category);
	}

	categoryIsTheBrief() {
		return this.props.category === "the-brief";
	}

	// Sitewide 1-day cookie; the nag unmounts its visuals entirely.
	dismiss() {
		setCookie(DISMISS_COOKIE, "1", DISMISS_DAYS);
		this.setState({ dismissed: true }, () => this.hide());
	}

	hide() {
		this.cleanup();
		if (this.base) this.base.style.display = "none";
	}

	cleanup() {
		if (this.raf) cancelAnimationFrame(this.raf);
		this.raf = null;
		if (this.resizeObserver) this.resizeObserver.disconnect();
		this.resizeObserver = null;
		window.removeEventListener("resize", this.onResize);
		window.removeEventListener("scroll", this.onScroll);
	}

	onResize() {
		this.position();
	}

	// rAF-throttled reposition — RO + resize both funnel here.
	position() {
		if (this.raf) return;
		this.raf = requestAnimationFrame(() => {
			this.raf = null;
			if (!this.base || !this.anchor || !this.contentEl) return;
			const top =
				this.anchor.getBoundingClientRect().bottom -
				this.contentEl.getBoundingClientRect().top +
				GAP;
			this.base.style.top = `${Math.max(0, Math.round(top))}px`;
		});
	}

	renderContent() {
		if (this.state.dismissed || this.dismissed() || !this.eligible()) return null;

		let title = 'Get the Brief for Free';
		let description = 'Sign up for our free morning newsletter, in which Executive Editor John Light starts your day with some quick thoughts on key stories TPM will be following.';


		if (!this.categoryIsTheBrief()) {
			title = 'Sign up for TPM’s Free Morning Newsletter';
			description = 'In The Brief, Executive Editor John Light starts your day with some quick thoughts on key stories TPM will be following.';
		}

		return (
			<div className="EmailNag__Inner">
				<div className="EmailNag__X" onClick={this.dismiss}>
					<Icon name="xmark" style="light" />
				</div>
				<NewsletterSignupBlock
					category={'the-brief'} 
					categories={EMAILNAG_CATEGORIES}
					cssclasses="--emailnag --red"
					title={title}
					description={description}
				/>
				<button className="EmailNag__Dismiss" onClick={this.dismiss}>
					Continue Reading
				</button>
			</div>
		);
	}
}
